import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router'
import pluralize from 'pluralize'
import { Fragment, useCallback, useMemo, useState } from 'react'
import { AppLoader } from '~/components/loader'
import { WorkspacesCommandList } from '~/features/command-lists'
import { useDatastore } from '~/features/datastore'
import { FILTER_FIELD_OPTIONS } from '~/features/filter-control/constants'
import { RoleMutators } from '~/mutators/roles'
import { RouterOutput, useTRPC } from '~/services/trpc'
import { SettingsContent, type TitleSegment, useSettingTitle } from '~/templates/settings'

import { FilterCollection } from '@hap/analytics'
import {
  Alert,
  AlertDialog,
  AlertDialogTrigger,
  AlertTitle,
  Badge,
  Button,
  Heading2,
  Heading3,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@hap/components'
import { HomepageView, Roles } from '@hap/constants'

import { Resource } from './-components/resources'
import { RoleInfoForm } from './-components/role-info-form'

import { useQuery } from "@tanstack/react-query";

export const Route = createFileRoute('/_authenticated/$alias/settings/roles/$roleId')({
  component: RouteComponent,
})

function RouteComponent() {
  const trpc = useTRPC();
  const { roleId } = Route.useParams()
  const location = useLocation()
  const navigate = useNavigate({ from: '/$alias/settings' })
  const { data: role } = useQuery(trpc.roles.fromId.queryOptions(roleId))
  const { data: workspaces } = useDatastore(FilterCollection.Workspaces)

  const { update } = RoleMutators.useUpdate()

  const navigateToRoles = useCallback(() => {
    navigate({ to: '/$alias/settings/roles', search: location.search })
  }, [location.search, navigate])

  const titleSegments: TitleSegment[] = useMemo(
    () =>
      [{ label: 'Roles', onClick: navigateToRoles }, role?.name].filter(
        Boolean,
      ) as TitleSegment[],
    [navigateToRoles, role?.name],
  )

  useSettingTitle(titleSegments)

  if (!role) return <AppLoader />

  return (
    <SettingsContent className="w-full overflow-y-auto">
      {role.isSystemRole && (
        <Alert variant="warn">
          <AlertTitle>This is a system default role and cannot be edited.</AlertTitle>
        </Alert>
      )}

      <header className="flex items-start">
        <hgroup className="shrink grow">
          <RoleInfoForm
            values={{
              name: role.name,
              description: role.description ?? null,
              homepageDefault: role.homepageDefault as unknown as HomepageView,
            }}
            roleId={role.id}
            disabled={role.isSystemRole}
          />
        </hgroup>
      </header>

      <RoleFeatureFlags role={role} disabled={role.isSystemRole} />

      {modules.map((module, key) => {
        return (
          <section className="space-y-6" key={key}>
            <Heading2>{module.name}</Heading2>
            <ul className="bg-accent rounded-md px-4">
              {module.resources.map((resource, key) => {
                return (
                  <Resource
                    key={key}
                    resource={resource}
                    action={
                      role.permissions.find((p) => p.resource === resource.resource)
                        ?.action as Roles.Permission
                    }
                    roleId={role.id}
                    disabled={role.isSystemRole}
                  />
                )
              })}
            </ul>
          </section>
        )
      })}

      <section className="space-y-4">
        <Heading2>Resource permissions</Heading2>
        {!role.isSystemRole ? (
          <div className="pt-2 pb-4">
            <div className="bg-primary/10 items-center space-y-2 rounded-md p-4">
              <div className="space-y-1">
                <Heading3>Scope resource permissions</Heading3>
                <p className="text-accent-foreground">
                  Lock resource permissions down to specific workspaces. Permissions are
                  organization-wide if no workspace is selected.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {role.workspaceIds?.map((workspaceId) => {
                  const workspace = workspaces.find(
                    (workspace) => workspace.id === workspaceId,
                  )
                  if (!workspace) return null
                  return (
                    <Badge key={workspaceId} className="gap-2 pr-0">
                      <span>{workspace.name}</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            onClick={async () => {
                              const workspaceIds = role.workspaceIds || []
                              await update({
                                roleId: role.id,
                                values: {
                                  workspaceIds: workspaceIds.filter(
                                    (id) => id !== workspaceId,
                                  ),
                                },
                              })
                            }}
                            variant="ghost"
                            icon="x"
                            size="icon-sm"
                          />
                        </TooltipTrigger>
                        <TooltipContent>Remove workspace</TooltipContent>
                      </Tooltip>
                    </Badge>
                  )
                })}
                <Popover>
                  <PopoverTrigger>
                    <Button
                      icon="plus"
                      variant="outline"
                      size={role.workspaceIds?.length ? 'icon-sm' : 'sm'}
                    >
                      {role.workspaceIds?.length ? null : 'Workspace'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0">
                    <WorkspacesCommandList
                      multiselect
                      selected={role.workspaceIds || []}
                      onSelect={async (workspaceIds) => {
                        await update({
                          roleId: role.id,
                          values: { workspaceIds },
                        })
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        ) : (
          <div className="pb-4" />
        )}

        {workspaceModules.map((module) => {
          return (
            <Fragment key={module.name}>
              <section>
                <Heading2>{module.name}</Heading2>
                <ul>
                  {module.resources.map((resource, key) => {
                    return (
                      <Resource
                        key={key}
                        resource={resource}
                        action={
                          role.permissions.find((p) => p.resource === resource.resource)
                            ?.action as Roles.Permission
                        }
                        roleId={role.id}
                        disabled={role.isSystemRole}
                      />
                    )
                  })}
                </ul>
              </section>
            </Fragment>
          )
        })}

        <section className="space-y-6">
          <Heading2>Analytics</Heading2>
          <ul className="bg-accent rounded-md px-4">
            {analyticsModules[0].resources.map((resource, key) => {
              return (
                <Resource
                  key={key}
                  resource={resource}
                  action={
                    role.permissions.find((p) => p.resource === resource.resource)
                      ?.action as Roles.Permission
                  }
                  roleId={role.id}
                  disabled={role.isSystemRole}
                />
              )
            })}
            <RoleFilterAccess role={role} disabled={role.isSystemRole} />
          </ul>
        </section>
      </section>

      {!role.isSystemRole && <DangerZone role={role} onDeleted={navigateToRoles} />}
    </SettingsContent>
  )
}

function DangerZone(props: {
  role: RouterOutput['roles']['fromId']
  onDeleted: () => void
}) {
  const { destroy } = RoleMutators.useDestroy()
  const [deleteOpen, setDeleteOpen] = useState(false)

  const memberCount = props.role.memberCount
  const deleteDescription =
    memberCount > 0
      ? `This will affect ${memberCount} ${pluralize('member', memberCount)} currently assigned to this role. They will lose their permissions. This action cannot be undone.`
      : 'No members are currently assigned to this role. This action cannot be undone.'

  return (
    <div className="space-y-6 pb-12">
      <Heading2 className="text-destructive">Danger Zone</Heading2>

      <div className="border-destructive divide-y rounded-lg border">
        <div className="flex items-center justify-between gap-6 p-4">
          <div className="shrink grow space-y-1">
            <Label className="font-semibold">Delete this role</Label>
            <p className="text-accent-foreground">
              Permanently delete this role.
              {memberCount > 0
                ? ` ${memberCount} ${pluralize('member', memberCount)} currently assigned to this role will lose their permissions.`
                : ' No members are currently assigned to this role.'}
            </p>
          </div>
          <AlertDialog
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            title={`Delete ${props.role.name}?`}
            description={deleteDescription}
            action={{
              variant: 'destructive',
              value: 'Delete role',
              onClick: async () => {
                await destroy({ roleId: props.role.id })
                props.onDeleted()
              },
            }}
          >
            <AlertDialogTrigger asChild>
              <Button variant="ghost-destructive">Delete role</Button>
            </AlertDialogTrigger>
          </AlertDialog>
        </div>
      </div>
    </div>
  )
}

function RoleFeatureFlags(props: {
  role: RouterOutput['roles']['fromId']
  disabled: boolean
}) {
  const { update } = RoleMutators.useUpdate()

  return (
    <section className="space-y-6">
      <Heading2>Features</Heading2>

      <div className="bg-accent space-y-0 divide-y rounded-lg">
        <div className="flex items-center justify-between gap-4 p-4">
          <div className="shrink grow space-y-1">
            <h3 className="text-lg font-semibold">Conversation Intelligence</h3>
            <p className="text-accent-foreground">
              Analyze transcribed conversations for sentiment, intent, topics, and
              generate summaries.
            </p>
          </div>
          <Switch
            checked={!!props.role.enableConversationIntelligence}
            disabled={props.disabled}
            onCheckedChange={async (checked) => {
              await update({
                roleId: props.role.id,
                values: {
                  enableConversationIntelligence: checked ? new Date() : null,
                },
              })
            }}
          />
        </div>

        <div className="flex items-center justify-between gap-4 p-4">
          <div className="shrink grow space-y-1">
            <h3 className="text-lg font-semibold">Auto QA</h3>
            <p className="text-accent-foreground">
              Automatically evaluate interactions against assigned scorecards.
            </p>
          </div>
          <Switch
            checked={!!props.role.enableAutoQA}
            disabled={props.disabled}
            onCheckedChange={async (checked) => {
              await update({
                roleId: props.role.id,
                values: {
                  enableAutoQA: checked ? new Date() : null,
                },
              })
            }}
          />
        </div>
      </div>
    </section>
  )
}

function RoleFilterAccess(props: {
  role: RouterOutput['roles']['fromId']
  disabled: boolean
}) {
  const { update } = RoleMutators.useUpdate({ scopeId: `role-filters-${props.role.id}` })
  const allowedFields = props.role.allowedFilterFields
  const access = allowedFields === null ? 'all' : 'limited'
  const limitedFields = allowedFields ?? []
  const knownFields = FILTER_FIELD_OPTIONS.map(({ field }) => field)

  return (
    <>
      <li className="flex items-center border-b py-4 last-of-type:border-b-0">
        <div className="grow">
          <h3 className="text-lg font-semibold">Filter Access</h3>
          <p className="text-accent-foreground">Control which filter fields are accessible</p>
        </div>
        <div className="shrink-0">
          <Select
            value={access}
            disabled={props.disabled}
            onValueChange={async (value) => {
              await update({
                roleId: props.role.id,
                values: {
                  allowedFilterFields: value === 'all' ? null : knownFields,
                },
              }).catch(() => undefined)
            }}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="limited">Limited</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </li>

      {access === 'limited' && (
        <li className="border-b py-4 last-of-type:border-b-0">
          <div className="grid overflow-hidden rounded-lg border md:grid-cols-2">
            {FILTER_FIELD_OPTIONS.map((option, index) => {
              const checked = limitedFields.includes(option.field)
              return (
                <div
                  key={option.field}
                  className={cn(
                    'flex min-h-14 items-center justify-between gap-4 px-4 py-3',
                    index > 0 && 'border-t',
                    index === 1 && 'md:border-t-0',
                    index % 2 === 1 && 'md:border-l',
                  )}
                >
                  <Label htmlFor={`filter-${option.field}`} className="grow">
                    {option.label}
                  </Label>
                  <Switch
                    id={`filter-${option.field}`}
                    checked={checked}
                    disabled={props.disabled}
                    onCheckedChange={async (nextChecked) => {
                      const currentFields = limitedFields.filter((field) =>
                        knownFields.includes(field),
                      )
                      const nextFields = nextChecked
                        ? [...new Set([...currentFields, option.field])]
                        : currentFields.filter((field) => field !== option.field)

                      await update({
                        roleId: props.role.id,
                        values: { allowedFilterFields: nextFields },
                      }).catch(() => undefined)
                    }}
                  />
                </div>
              )
            })}
          </div>
        </li>
      )}
    </>
  )
}

const modules = [
  {
    name: 'Management permissions',
    resources: [
      {
        name: 'Goals',
        description: 'Can modify organization-level goals',
        resource: Roles.ResourceType.Goals,
        scope: Roles.PermissionScope.All,
        actions: [
          {
            action: Roles.Permission.Manage,
            description: 'Can create, edit, and delete goals',
          },
          { action: Roles.Permission.None, description: 'Cannot manage goals' },
        ],
      },
      {
        name: 'Organization',
        description: 'Can manage organization profile, URL, and integrations',
        resource: Roles.ResourceType.Organizations,
        scope: Roles.PermissionScope.All,
        actions: [
          {
            action: Roles.Permission.Manage,
            description: 'Can manage organization settings and integrations',
          },
          {
            action: Roles.Permission.None,
            description: 'Cannot manage organization settings',
          },
        ],
      },
      {
        name: 'Billing',
        description: 'Can access and manage payment information and invoices',
        resource: Roles.ResourceType.Billing,
        scope: Roles.PermissionScope.All,
        actions: [
          {
            action: Roles.Permission.Manage,
            description: 'Can access and manage payment information and invoices',
          },
          { action: Roles.Permission.None, description: 'Cannot access billing info' },
        ],
      },
      {
        name: 'Flags',
        description: 'Can create, edit, and delete flags',
        resource: Roles.ResourceType.Flags,
        scope: Roles.PermissionScope.All,
        actions: [
          {
            action: Roles.Permission.Manage,
            description: 'Can create, edit, and delete flags',
          },
          { action: Roles.Permission.None, description: 'Cannot manage flags' },
        ],
      },
      {
        name: 'Channels',
        description: 'Can configure, assign, and delete channels',
        resource: Roles.ResourceType.Channels,
        scope: Roles.PermissionScope.All,
        actions: [
          {
            action: Roles.Permission.Manage,
            description: 'Can configure, edit, and delete channels',
          },
          { action: Roles.Permission.None, description: 'Cannot manage channels' },
        ],
      },
      {
        name: 'Workspaces',
        description: 'Can create, modify, or delete workspaces',
        resource: Roles.ResourceType.Workspaces,
        scope: Roles.PermissionScope.All,
        actions: [
          {
            action: Roles.Permission.Manage,
            description: 'Can create, edit, and delete workspaces',
          },
          { action: Roles.Permission.None, description: 'Cannot manage workspaces' },
        ],
      },
      {
        name: 'Members',
        description: 'Can invite, edit, or remove members',
        resource: Roles.ResourceType.Members,
        scope: Roles.PermissionScope.All,
        actions: [
          {
            action: Roles.Permission.Manage,
            description: 'Can invite, edit, or remove members',
          },
          { action: Roles.Permission.None, description: 'Cannot manage members' },
        ],
      },
      {
        name: 'Impersonation',
        description: 'Can impersonate members for support and troubleshooting',
        resource: Roles.ResourceType.Impersonation,
        scope: Roles.PermissionScope.All,
        actions: [
          {
            action: Roles.Permission.Manage,
            description: 'Can impersonate active members',
          },
          {
            action: Roles.Permission.None,
            description: 'Cannot impersonate members',
          },
        ],
      },
      {
        name: 'Groups',
        description: 'Can create, modify, or delete groups',
        resource: Roles.ResourceType.Groups,
        scope: Roles.PermissionScope.All,
        actions: [
          { action: Roles.Permission.Manage },
          { action: Roles.Permission.None, description: 'Cannot manage groups' },
        ],
      },
      {
        name: 'Roles',
        resource: Roles.ResourceType.Roles,
        description: 'Can create, modify, or delete roles',
        scope: Roles.PermissionScope.All,
        actions: [
          {
            action: Roles.Permission.Manage,
            description: 'Can create, edit, and delete roles',
          },
          { action: Roles.Permission.None, description: 'Cannot access roles' },
        ],
      },
    ],
  },
]

const analyticsModules = [
  {
    name: 'Analytics',
    resources: [
      {
        name: 'Leaderboard',
        description: 'Can view leaderboards and agent performance rankings',
        resource: Roles.ResourceType.Leaderboard,
        scope: Roles.PermissionScope.All,
        actions: [
          {
            action: Roles.Permission.ViewAll,
            description: 'Can view leaderboards',
          },
          { action: Roles.Permission.None, description: 'Cannot view leaderboards' },
        ],
      },
    ],
  },
]

const workspaceModules = [
  {
    name: 'Interactions',
    resources: [
      {
        name: 'Interactions',
        resource: Roles.ResourceType.InteractionsViewAll,
        scope: Roles.PermissionScope.All,
        actions: [
          {
            action: Roles.Permission.Manage,
            description: 'Can modify and delete interactions',
          },
          { action: Roles.Permission.ViewAll, description: 'Can view all interactions' },
          {
            action: Roles.Permission.ViewLimited,
            description: 'Can only view interactions they handled',
          },
          { action: Roles.Permission.None, description: 'Cannot view any interactions' },
        ],
      },
      {
        name: 'Export data',
        scope: Roles.PermissionScope.All,
        resource: Roles.ResourceType.InteractionsExport,
        actions: [
          {
            action: Roles.Permission.Manage,
            description: 'Can export interactions, recordings, and transcripts',
          },
          {
            action: Roles.Permission.None,
            description: 'Cannot export interactions, recordings, or transcripts',
          },
        ],
      },
    ],
  },
  {
    name: 'Evaluations',
    resources: [
      {
        name: 'Evaluation visibility',
        scope: Roles.PermissionScope.All,
        resource: Roles.ResourceType.EvaluationsViewAll,
        actions: [
          { action: Roles.Permission.ViewAll, description: 'Can view all evaluations' },
          {
            action: Roles.Permission.ViewLimited,
            description: 'Can only view evaluations assigned to them',
          },
          { action: Roles.Permission.None, description: 'Cannot view any evaluations' },
        ],
      },
      {
        name: 'Evaluate interactions',
        scope: Roles.PermissionScope.All,
        resource: Roles.ResourceType.Evaluate,
        actions: [
          {
            action: Roles.Permission.Manage,
            description: 'Can create, edit, and delete evaluations',
          },
          { action: Roles.Permission.None, description: 'Cannot modify any evaluations' },
        ],
      },
    ],
  },
  {
    name: 'Scorecards',
    resources: [
      {
        name: 'Scorecard Management',
        scope: Roles.PermissionScope.All,
        resource: Roles.ResourceType.Scorecards,
        actions: [
          {
            action: Roles.Permission.Manage,
            description: 'Can create, edit, and delete scorecards',
          },
          { action: Roles.Permission.None, description: 'Cannot access scorecards' },
        ],
      },
    ],
  },
]
